import React, { useEffect, useState,useRef } from "react";
import { Box, Drawer, IconButton, Button, Select, MenuItem, FormControl, InputLabel } from "@mui/material";
import { useTranslation } from "react-i18next";
import profilecross from "../../../../assets/profilecross.svg";
import CustomText, { textStyles } from "../../../../components/CustomText";
import CustomButton from "../../../../components/CustomButton";
import save from "../../../../assets/save.svg";

// 🔹 Category Options
const categoryOptions = [
  { key: "medical", label: "Medical", value: "medical" },
  { key: "id", label: "ID", value: "id" },
  { key: "expense", label: "Expense", value: "expense" },
  { key: "other", label: "Other", value: "other" },
];

// 🔹 Status Options — must match the backend's documents.status CHECK constraint
// (active, expired, revoked); "inactive" is not a valid document status.
// Labels are translated via `statuses.<key>` at render time.
const statusOptions = [
  { key: "active", label: "Active", value: "active" },
  { key: "expired", label: "Expired", value: "expired" },
  { key: "revoked", label: "Revoked", value: "revoked" },
];

// 🔹 Sort By Options — labels are translated via `sortByLabelKeys` at render time.
const sortByOptions = [
  { key: "created_at", label: "Created Date", value: "created_at" },
  { key: "updated_at", label: "Updated Date", value: "updated_at" },
  { key: "name", label: "Document Name", value: "name" },
  // { key: "category", label: "Category", value: "category" },
  { key: "status", label: "Status", value: "status" },
  { key: "file_type", label: "File Type", value: "file_type" },
];

const sortByLabelKeys = {
  created_at: "documentsTable.filter.sortByOptions.createdDate",
  updated_at: "documentsTable.filter.sortByOptions.updatedDate",
  name: "documentsTable.th.documentName",
  status: "documentsTable.th.status",
  file_type: "documentsTable.th.fileType",
};

const FilterDrawer = ({ open, onClose, filters, setFilters, onApplyFilters }) => {
  const { t } = useTranslation();
  const [localFilters, setLocalFilters] = useState(filters);
  const startRef = useRef(null);
  const endRef = useRef(null);
  useEffect(() => {
    if (open) {
      setLocalFilters({ ...filters });
    }
  }, [open, filters]);

  const apply = () => {
    console.log("✅ Applying filters:", localFilters);
    onApplyFilters(localFilters);
    onClose();
  };

  const clear = () => {
    const cleared = { 
      startDate: "", 
      endDate: "", 
      status: "",
      sortBy: "created_at",
      sortOrder: "desc"
    };
    console.log("♻️ Clearing filters:", cleared);
    setLocalFilters(cleared);
    onApplyFilters(cleared);
    onClose();
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 400, md: 550 },
          backgroundColor: "#E7EBEE",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          backgroundColor: "#E7EBEE",
          zIndex: 10,
          px: 2,
          py: 1.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton onClick={onClose}>
            <img src={profilecross} alt="close" />
          </IconButton>
          <CustomText
            sx={{
              ...textStyles.h1,
              fontSize: 20,
              fontWeight: 600,
              color: "#2C384C",
            }}
          >
            {t("documentsTable.filter.heading") || "Filters"}
          </CustomText>
        </Box>
      </Box>

      {/* Body */}
      <Box sx={{ px: 2 }}>
        <Box
          sx={{
            backgroundColor: "#FFFFFF",
            px: 3,
            py: 3,
            height: "calc(100vh - 90px)",
            overflowY: "auto",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          {/* Status Filter */}
          <Box sx={{ mt: 3 }}>
            <label className="field_label">{t("documentsTable.filter.status") || "Status"}</label>
            <FormControl fullWidth sx={{ mt: 1 }}>
              <Select
                value={localFilters.status || ""}
                onChange={(e) =>
                  setLocalFilters((prev) => ({
                    ...prev,
                    status: e.target.value,
                  }))
                }
                displayEmpty
                sx={{
                  minHeight: 40,
                  fontSize: "14px",
                  fontFamily: "Poppins, sans-serif",
                  backgroundColor: "#fff",
                  "& .MuiSelect-select": {
                    py: 1,
                    px: 1.5,
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#091E4224",
                  },
                }}
              >
                <MenuItem 
                  value=""
                  sx={{
                    fontSize: "14px",
                    fontFamily: "Poppins, sans-serif",
                  }}
                >
                  <em style={{ color: "#999", fontStyle: "italic" }}>
                    {t("documentsTable.filter.selectStatus") || "Select Status"}
                  </em>
                </MenuItem>
                {statusOptions.map((opt) => (
                  <MenuItem
                    key={opt.key}
                    value={opt.value}
                    sx={{
                      fontSize: "14px",
                      fontFamily: "Poppins, sans-serif",
                    }}
                  >
                    {t(`statuses.${opt.key}`, opt.label)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Date From */}
          <Box sx={{ mt: 3 }}>
            <label className="field_label">{t("documentsTable.filter.startDate") || "Start Date"}</label>
            <input
             ref={startRef}
                onClick={() => startRef.current?.showPicker()} 
              type="date"
              value={localFilters.startDate || ""}
              onChange={(e) =>
                setLocalFilters((prev) => ({
                  ...prev,
                  startDate: e.target.value,
                }))
              }
              className="project_input"
              style={{ width: "100%", height: 40, marginTop: 6 }}
            />
          </Box>

          {/* Date To */}
          <Box sx={{ mt: 3 }}>
            <label className="field_label">{t("documentsTable.filter.endDate") || "End Date"}</label>
            <input
              ref={endRef}
              onClick={() => endRef.current?.showPicker()} 
              type="date"
              value={localFilters.endDate || ""}
              onChange={(e) =>
                setLocalFilters((prev) => ({
                  ...prev,
                  endDate: e.target.value,
                }))
              }
              className="project_input"
              style={{ width: "100%", height: 40, marginTop: 6 }}
            />
          </Box>

          {/* Sort By */}
          <Box sx={{ mt: 3 }}>
            <label className="field_label">{t("documentsTable.filter.sortBy") || "Sort By"}</label>
            <FormControl fullWidth sx={{ mt: 1 }}>
              <Select
                value={localFilters.sortBy || "created_at"}
                onChange={(e) =>
                  setLocalFilters((prev) => ({
                    ...prev,
                    sortBy: e.target.value,
                  }))
                }
                sx={{
                  minHeight: 40,
                  fontSize: "14px",
                  fontFamily: "Poppins, sans-serif",
                  backgroundColor: "#fff",
                  "& .MuiSelect-select": {
                    py: 1,
                    px: 1.5,
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#091E4224",
                  },
                }}
              >
                {sortByOptions.map((opt) => (
                  <MenuItem
                    key={opt.key}
                    value={opt.value}
                    sx={{
                      fontSize: "14px",
                      fontFamily: "Poppins, sans-serif",
                    }}
                  >
                    {t(sortByLabelKeys[opt.key], opt.label)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Sort Order */}
          <Box sx={{ mt: 3 }}>
            <label className="field_label">{t("documentsTable.filter.sortOrder") || "Sort Order"}</label>
            <FormControl fullWidth sx={{ mt: 1 }}>
              <Select
                value={localFilters.sortOrder || "desc"}
                onChange={(e) =>
                  setLocalFilters((prev) => ({
                    ...prev,
                    sortOrder: e.target.value,
                  }))
                }
                sx={{
                  minHeight: 40,
                  fontSize: "14px",
                  fontFamily: "Poppins, sans-serif",
                  backgroundColor: "#fff",
                  "& .MuiSelect-select": {
                    py: 1,
                    px: 1.5,
                  },
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "#091E4224",
                  },
                }}
              >
                <MenuItem
                  value="asc"
                  sx={{
                    fontSize: "14px",
                    fontFamily: "Poppins, sans-serif",
                  }}
                >
                  {t("documentsTable.filter.ascending") || "Ascending"}
                </MenuItem>
                <MenuItem
                  value="desc"
                  sx={{
                    fontSize: "14px",
                    fontFamily: "Poppins, sans-serif",
                  }}
                >
                  {t("documentsTable.filter.descending") || "Descending"}
                </MenuItem>
              </Select>
            </FormControl>
          </Box>

          {/* Buttons */}
          <Box sx={{ mt: 4, display: "flex", flexWrap: "wrap", gap: 2 }}>
            <CustomButton
              onClick={apply}
              label={
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <img src={save} alt="save" style={{ width: 16, height: 16 }} />
                  {t("documentsTable.filter.apply") || "Apply"}
                </Box>
              }
              size="medium"
            />
            <Button
              variant="outlined"
              onClick={clear}
              sx={{
                fontSize: 15,
                textTransform: "none",
                borderRadius: "5px",
                fontFamily: "Poppins, sans-serif",
                color: "#006EC2",
                border: "2px solid #006EC2",
                px: 3,
                py: 1,
                height: "fit-content",
              }}
            >
              {t("documentsTable.filter.clear") || "Clear"}
            </Button>
          </Box>
        </Box>
      </Box>
    </Drawer>
  );
};

export default FilterDrawer;
